import { PRINT_BLEED_TINT_INSIDE_TRIM_PT } from '@/lib/document/print-bleed'
import { computeHalfTrimWidth } from '../../helpers/page-trim-width'
import { BLEED_TINT_FILL } from '../constants/bleed-tint-fill'

type SinglePageSpineBleedStripProps = {
  bleedHeight: number
  bleedMargin: number
  trimHeight: number
  /** Full spread trim width (same basis as `PrintBleedTintOverlay`). */
  trimWidth: number
  singlePageSide: 'left' | 'right'
  /** Same base z-index as `SpineGutterOverlay` tint; below layout grid (z-10). */
  zIndexBase?: number
  /** See `PrintBleedTints` — gates all three vertically-stacked pieces, fed from the editor's
   * "Show Bleed Bar" toggle. */
  showOutsideTrim: boolean
}

/**
 * Spine-edge strip for first/last single-page views. Matches `SpineGutterOverlay` fill and full
 * `bleedHeight` so it meets the top and bottom bleed bands. Split into three vertically stacked
 * pieces — top bleed-margin, trim-height middle, bottom bleed-margin — matching
 * `DoubleSpreadGutterTintAboveShapes`'s equivalent split for the double-spread gutter.
 */
export function SinglePageSpineBleedStrip({
  bleedHeight,
  bleedMargin,
  trimHeight,
  trimWidth,
  singlePageSide,
  zIndexBase = 8,
  showOutsideTrim,
}: SinglePageSpineBleedStripProps) {
  const insideTint = PRINT_BLEED_TINT_INSIDE_TRIM_PT
  const pageTrimWidth = computeHalfTrimWidth(trimWidth)
  const spineX = bleedMargin + pageTrimWidth
  const left = singlePageSide === 'left' ? spineX - insideTint : spineX

  if (bleedHeight <= 0 || insideTint <= 0) {
    return null
  }

  const base = {
    zIndex: zIndexBase,
    left,
    width: insideTint,
    ...BLEED_TINT_FILL,
  } as const

  return (
    <>
      {showOutsideTrim && (
        <div
          className="doc-bleed-tint pointer-events-none absolute"
          style={{ ...base, top: 0, height: bleedMargin }}
        />
      )}
      {showOutsideTrim && (
        <div
          className="doc-bleed-tint pointer-events-none absolute"
          style={{ ...base, top: bleedMargin, height: trimHeight }}
        />
      )}
      {showOutsideTrim && (
        <div
          className="doc-bleed-tint pointer-events-none absolute"
          style={{
            ...base,
            top: bleedMargin + trimHeight,
            height: bleedHeight - bleedMargin - trimHeight,
          }}
        />
      )}
    </>
  )
}
